import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Modal, Spinner, Table } from "components/ui/BootstrapCompat";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { FiArrowLeft, FiCheck, FiCopy, FiEdit3, FiPlus, FiRefreshCw, FiSave, FiSliders, FiTarget, FiTrash2 } from "react-icons/fi";
import useLocalStorage from "helpers/useLocalStorage";
import { errorAdd, successAdd } from "helpers/sweetalert";
import { userGetMe } from "services/user.service";
import { DONATE_BUSINESS_ID, DonateWidget, donateErrorMessage, donateWidgetCreateOne, donateWidgetGetMany, donateWidgetSendAlert, donateWidgetSetGoal, donateWidgetUpdateOne } from "services/main/donate.service";
import type { AlertFormState, AlertImageUploadField, CreateWidgetFormState, FlowContextMenuState, FlowEditorNode, FlowEditorState, FlowGridItem, FlowNodeKind, ResizeHandle, WidgetAnchor, WidgetConfigState, WidgetScreen, WidgetTab } from "./donation-widget-config/types";
import { DEFAULT_CONFIG, ENABLED_WIDGET_TABS, RGL_COLS, RGL_CONTAINER_PADDING, RGL_MARGIN, RGL_ROW_HEIGHT, STORAGE_KEY, WIDGET_DEFINITIONS, enabledFlowNodePalette, resizeHandles, tabs, viewportPresets } from "./donation-widget-config/constants";
import { uploadDonationWidgetImage } from "./donation-widget-config/upload";
import { buildWidgetPayload, createDefaultFlowState, mergeWidgetConfigFromApi, normalizeGridState } from "./donation-widget-config/flowUtils";
import { findWidgetForTab, getPaymentMethodLabel, getPaymentMethodValue, getUserData, getWidgetData, getWidgetId, getWidgetPublicRouteUrl, getWidgetTab, getWidgetTypeLabel, isEnabledDonateWidget, isEnabledWidgetTab, normalizeWidgetList, readDraft } from "./donation-widget-config/widgetUtils";
import { TextInput } from "./donation-widget-config/FormControls";
import { WidgetListScreen } from "./donation-widget-config/WidgetListScreen";
import { WidgetEditorScreen } from "./donation-widget-config/WidgetEditorScreen";

export default function DonationWidgetConfig() {
  const queryClient = useQueryClient();
  const [token] = useLocalStorage("token", "");
  const [storedBusinessId] = useLocalStorage("businessId", "");
  const businessId = storedBusinessId || DONATE_BUSINESS_ID;
  const [activeTab, setActiveTab] = useState<WidgetTab>("alert");
  const [screen, setScreen] = useState<WidgetScreen>("list");
  const [selectedWidget, setSelectedWidget] = useState<DonateWidget | undefined>();
  const [isCreateWidgetOpen, setIsCreateWidgetOpen] = useState(false);
  const [createWidgetForm, setCreateWidgetForm] = useState<CreateWidgetFormState>({
    name: "",
    description: "",
  });
  const [editWidgetForm, setEditWidgetForm] = useState<CreateWidgetFormState>({
    name: "",
    description: "",
  });
  const [config, setConfig] = useState<WidgetConfigState>(() => readDraft());
  const [flowState, setFlowState] = useState<FlowEditorState>(() => createDefaultFlowState("alert", readDraft()));
  const [selectedFlowNodeId, setSelectedFlowNodeId] = useState("");
  const [copiedUrl, setCopiedUrl] = useState("");
  const hasHydratedWidgetsRef = useRef(false);
  const [timerSeconds, setTimerSeconds] = useState(Number(DEFAULT_CONFIG.timer.durationMinutes) * 60);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [alertForm, setAlertForm] = useState<AlertFormState>({
    name: "ໂຈໂຈ້",
    amount: "25000",
    paymentMethod: "",
    message: "ຂໍໃຫ້ stream ມ່ວນໆ",
  });

  const { data: userResponse, isLoading: isUserLoading } = useQuery(["user-me", businessId || "self"], () => userGetMe({ token, businessId }), { enabled: Boolean(token) });

  const {
    data: widgetResponse,
    isLoading: isWidgetLoading,
    isFetching: isWidgetFetching,
    refetch: refetchWidgets,
  } = useQuery(
    ["donate-widgets", businessId],
    () =>
      donateWidgetGetMany({
        token,
        businessId,
        limit: 100,
        sortBy: "updatedAt",
        order: "desc",
      }),
    { enabled: Boolean(token && businessId), retry: false },
  );

  const user = useMemo(() => getUserData(userResponse), [userResponse]);
  const widgetList = useMemo(() => normalizeWidgetList(widgetResponse), [widgetResponse]);
  const enabledWidgetList = useMemo(() => widgetList.filter(isEnabledDonateWidget), [widgetList]);
  const widgetByTab = useMemo<Record<WidgetTab, DonateWidget | undefined>>(
    () => ({
      alert: findWidgetForTab(widgetList, "alert"),
      goal: findWidgetForTab(widgetList, "goal"),
      timer: findWidgetForTab(widgetList, "timer"),
      leaderboard: findWidgetForTab(widgetList, "leaderboard"),
      chat: findWidgetForTab(widgetList, "chat"),
    }),
    [widgetList],
  );
  const activeWidget = selectedWidget || widgetByTab[activeTab];
  const userId = user?._id || user?.id || user?.userId || "";

  useEffect(() => {
    hasHydratedWidgetsRef.current = false;
  }, [businessId]);

  useEffect(() => {
    if (hasHydratedWidgetsRef.current || !widgetList.length) return;
    setConfig((current) => mergeWidgetConfigFromApi(current, widgetList));
    hasHydratedWidgetsRef.current = true;
  }, [widgetList]);

  useEffect(() => {
    if (!selectedWidget) return;

    const selectedId = getWidgetId(selectedWidget);
    const nextWidget = widgetList.find((widget) => getWidgetId(widget) === selectedId);

    if (nextWidget && nextWidget !== selectedWidget) {
      setSelectedWidget(nextWidget);
    }
  }, [selectedWidget, widgetList]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }, [config]);

  useEffect(() => {
    const nextSeconds = Math.max(Number(config.timer.durationMinutes) || 0, 0);
    setTimerSeconds(nextSeconds * 60);
  }, [config.timer.durationMinutes]);

  useEffect(() => {
    if (!isTimerRunning) return undefined;

    const intervalId = window.setInterval(() => {
      setTimerSeconds((seconds) => {
        if (seconds <= 0) {
          setIsTimerRunning(false);
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isTimerRunning]);

  const updateConfig = <Section extends keyof WidgetConfigState>(section: Section, patch: Partial<WidgetConfigState[Section]>) => {
    setConfig((current) => ({
      ...current,
      [section]: {
        ...current[section],
        ...patch,
      },
    }));
  };

  const copyText = async (text: string) => {
    if (!text) {
      errorAdd("ບໍ່ມີລິ້ງສຳລັບສຳເນົາ");
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopiedUrl(text);
      successAdd("ສຳເນົາລິ້ງສຳເລັດ");
      window.setTimeout(() => setCopiedUrl(""), 1400);
    } catch {
      errorAdd("ບໍ່ສາມາດສຳເນົາລິ້ງໄດ້");
    }
  };

  const openCreateWidgetModal = () => {
    setCreateWidgetForm({
      name: WIDGET_DEFINITIONS.alert.name,
      description: WIDGET_DEFINITIONS.alert.description,
    });
    setIsCreateWidgetOpen(true);
  };

  const closeCreateWidgetModal = () => {
    if (createWidgetMutation.isLoading) return;
    setIsCreateWidgetOpen(false);
  };

  const openWidgetEditor = (widget: DonateWidget) => {
    const nextTab = widget.type === "DONATION_ALERT" ? "alert" : getWidgetTab(widget);
    if (!isEnabledWidgetTab(nextTab)) {
      errorAdd("ຕອນນີ້ເປີດໃຊ້ສະເພາະ Donation Alert");
      return;
    }

    const nextConfig = mergeWidgetConfigFromApi(config, [widget]);
    const nextFlowState = normalizeGridState(widget.gridLayout, widget.reactFlow, nextTab, nextConfig);
    setSelectedWidget(widget);
    setEditWidgetForm({
      name: widget.name || WIDGET_DEFINITIONS[nextTab].name,
      description: widget.description || WIDGET_DEFINITIONS[nextTab].description,
    });
    setActiveTab(nextTab);
    setConfig(nextConfig);
    setFlowState(nextFlowState);
    setSelectedFlowNodeId(String(nextFlowState.nodes[0]?.id || ""));
    setScreen("editor");
  };

  const openWidgetTester = (widget: DonateWidget) => {
    const widgetPublicUrl = getWidgetPublicRouteUrl(widget);
    if (!widgetPublicUrl) {
      errorAdd("ບໍ່ມີລິ້ງ widget ສຳລັບທົດສອບ");
      return;
    }

    const testUrl = new URL(widgetPublicUrl);
    testUrl.searchParams.set("test", "1");
    testUrl.searchParams.set("debug", "1");
    testUrl.searchParams.set("live", "0");
    testUrl.searchParams.set("testWidth", String(flowState.viewport.width));
    testUrl.searchParams.set("testHeight", String(flowState.viewport.height));
    window.open(testUrl.toString(), "_blank", "noopener,noreferrer");
  };

  const backToWidgetList = () => {
    setScreen("list");
    setSelectedWidget(undefined);
    setEditWidgetForm({ name: "", description: "" });
    setSelectedFlowNodeId("");
    setCopiedUrl("");
    setIsTimerRunning(false);
  };

  const createWidgetMutation = useMutation(
    async () => {
      if (!token || !businessId) {
        throw new Error("Token and Business ID are required");
      }

      const name = createWidgetForm.name.trim();
      const description = createWidgetForm.description.trim();

      if (!name) {
        throw new Error("Widget name is required");
      }

      return donateWidgetCreateOne({
        token,
        businessId,
        body: {
          ...buildWidgetPayload("alert", config, undefined, createDefaultFlowState("alert", config)),
          name,
          description: description || WIDGET_DEFINITIONS.alert.description,
        },
      });
    },
    {
      onSuccess: () => {
        successAdd("ສ້າງ widget ສຳເລັດ");
        setIsCreateWidgetOpen(false);
        setCreateWidgetForm({ name: "", description: "" });
        queryClient.invalidateQueries(["donate-widgets", businessId]);
      },
      onError: (error: any) => {
        errorAdd(donateErrorMessage(error));
      },
    },
  );

  const saveMutation = useMutation(
    async () => {
      if (!token || !businessId) {
        throw new Error("Token and Business ID are required");
      }

      const widgetId = getWidgetId(activeWidget);
      const name = editWidgetForm.name.trim();
      const description = editWidgetForm.description.trim();

      if (!name) {
        throw new Error("Widget name is required");
      }

      const body = {
        ...buildWidgetPayload(activeTab, config, activeWidget, flowState),
        name,
        description,
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));

      if (widgetId) {
        return donateWidgetUpdateOne({
          token,
          businessId,
          id: widgetId,
          body,
        });
      }

      return donateWidgetCreateOne({
        token,
        businessId,
        body,
      });
    },
    {
      onSuccess: (response) => {
        const savedWidget = getWidgetData(response);
        if (savedWidget.widgetUrl) {
          setCopiedUrl("");
        }
        if (getWidgetId(savedWidget)) {
          setSelectedWidget(savedWidget);
          setEditWidgetForm({
            name: savedWidget.name || "",
            description: savedWidget.description || "",
          });
        }
        successAdd("ບັນທຶກການປັບແຕ່ງວິດເຈັດສຳເລັດ");
        queryClient.invalidateQueries(["donate-widgets", businessId]);
        queryClient.invalidateQueries(["user-me"]);
      },
      onError: (error: any) => {
        errorAdd(donateErrorMessage(error));
      },
    },
  );

  const sendAlertMutation = useMutation(
    async () => {
      if (!userId) throw new Error("User ID is required");
      return donateWidgetSendAlert({
        token,
        businessId,
        userId,
        body: {
          name: alertForm.name,
          amount: Number(alertForm.amount || 0),
          paymentMethod: alertForm.paymentMethod,
          message: alertForm.message,
          template: config.alert.messageTemplate,
        },
      });
    },
    {
      onSuccess: () => successAdd("ສົ່ງ Alert ທົດສອບສຳເລັດ"),
      onError: (error: any) => errorAdd(donateErrorMessage(error)),
    },
  );

  const setGoalMutation = useMutation(
    async () => {
      if (!userId) throw new Error("User ID is required");
      return donateWidgetSetGoal({
        token,
        businessId,
        userId,
        body: {
          title: config.goal.title,
          current: Number(config.goal.current || 0),
          target: Number(config.goal.target || 0),
          endsAt: config.goal.endsAt,
        },
      });
    },
    {
      onSuccess: () => successAdd("ອັບເດດເປົ້າໝາຍສຳເລັດ"),
      onError: (error: any) => errorAdd(donateErrorMessage(error)),
    },
  );

  const resetTimer = () => {
    setTimerSeconds(Math.max(Number(config.timer.durationMinutes) || 0, 0) * 60);
    setIsTimerRunning(false);
  };

  if (screen === "list") {
    return (
      <WidgetListScreen
        isWidgetTableLoading={isWidgetLoading || isWidgetFetching}
        enabledWidgetList={enabledWidgetList}
        token={token}
        businessId={businessId}
        copiedUrl={copiedUrl}
        createWidgetForm={createWidgetForm}
        isCreateWidgetOpen={isCreateWidgetOpen}
        isCreateWidgetLoading={createWidgetMutation.isLoading}
        onRefresh={() => refetchWidgets()}
        onOpenCreateWidgetModal={openCreateWidgetModal}
        onCloseCreateWidgetModal={closeCreateWidgetModal}
        onCreateWidgetSubmit={() => createWidgetMutation.mutate()}
        onCreateWidgetFormChange={(patch) => setCreateWidgetForm((current) => ({ ...current, ...patch }))}
        onCopyText={copyText}
        onOpenWidgetEditor={openWidgetEditor}
        onOpenWidgetTester={openWidgetTester}
      />
    );
  }

  return (
    <WidgetEditorScreen
      activeWidget={activeWidget}
      widgetForm={editWidgetForm}
      flowState={flowState}
      config={config}
      selectedFlowNodeId={selectedFlowNodeId}
      isSaveDisabled={
        saveMutation.isLoading ||
        isUserLoading ||
        isWidgetLoading ||
        !token ||
        !businessId ||
        !editWidgetForm.name.trim()
      }
      isSaving={saveMutation.isLoading}
      onBack={backToWidgetList}
      onSave={() => saveMutation.mutate()}
      onWidgetFormChange={(patch) =>
        setEditWidgetForm((current) => ({ ...current, ...patch }))
      }
      onFlowChange={setFlowState}
      onSelectFlowNode={setSelectedFlowNodeId}
    />
  );
}
